import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box, Typography, Button, Table, TableHead, TableRow, TableCell,
    TableBody, Chip, Slider, CircularProgress, FormControl, Select,
    MenuItem, Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import EastIcon from '@mui/icons-material/East';
import SearchIcon from '@mui/icons-material/Search';
import { getAllPortfolios } from '../../../api/portfolio_api';
import { getStocksScreenerQuant } from '../../../api/stocks_api';
import AddToWatchlistDialog from '../../IndividualStocks/Widgets/AddToWatchlistDialog';
import { useTheme } from '@mui/material/styles';
import { ACTIVE_MODEL } from '../../../config/modelConfig';
import { BALANCE_SHEET_FACTOR } from '../../../config/factors';
const market = sessionStorage.getItem("selectedMarket") ?? "US";

/* ─────────────────────────────────────────────────────────────
   CONSTANTS
   Slider:  F=1 (left/loose)  →  A=5 (right/strict)
   null (APPLIED_NONE) = this filter has never been applied yet
   → Apply button is always enabled on first render
───────────────────────────────────────────────────────────── */
const GRADE_VAL_DEFAULT = 1;   // slider always initialises at F
const APPLIED_NONE = null; // sentinel — not yet applied

// const sliderToGrade = { 1: 'F', 2: 'D', 3: 'C', 4: 'B', 5: 'A' };
// const gradeToSlider = { F: 1, D: 2, C: 3, B: 4, A: 5 };

const sliderToGrade = { 1: 'F', 2: 'E', 3: 'D', 4: 'C', 5: 'B', 6: 'A' };
const gradeToSlider = { F: 1, E: 2, D: 3, C: 4, B: 5, A: 6 };

// const SLIDER_MARKS = [
//     { value: 1, label: 'F' },
//     { value: 2, label: 'D' },
//     { value: 3, label: 'C' },
//     { value: 4, label: 'B' },
//     { value: 5, label: 'A' },
// ];

const SLIDER_MARKS = [
    { value: 1, label: 'F' },
    { value: 2, label: 'E' },
    { value: 3, label: 'D' },
    { value: 4, label: 'C' },
    { value: 5, label: 'B' },
    { value: 6, label: 'A' },
];


/* grades that qualify when minimum = val  (val itself + everything to the right) */
const getIncludedGrades = (val) =>
    Object.entries(gradeToSlider)
        .filter(([, sv]) => sv >= val)
        .sort((a, b) => b[1] - a[1])
        .map(([g]) => g);

/* full sort rank for table — A+ best, F worst */
const gradeSortRank = (grade) => {
    const map = {
        'A+': 1, A: 2, 'A-': 3,
        'B+': 4, B: 5, 'B-': 6,
        'C+': 7, C: 8, 'C-': 9,
        'D+': 10, D: 11, 'D-': 12,
        // 'E+': 13, E: 14, 'E-': 15,  // ← new
        F: 16,
    };
    return (!grade || grade === '-') ? 999 : (map[grade] ?? 999);
};

/* ─────────────────────────────────────────────────────────────
   STATIC DATA
───────────────────────────────────────────────────────────── */
const sectors = [
    'Technology', 'Consumer Cyclical', 'Healthcare', 'Consumer Defensive',
    'Financial Services', 'Energy', 'Communication Services', 'Industrials',
    'Basic Materials', 'Utilities', 'Real Estate',
];

const filterCategories = [
    { name: 'VALUATION', key: 'valuation_grade' },
    { name: 'GROWTH', key: 'growth_grade' },
    // { name: 'PROFITABILITY', key: 'financial_profitability_grade' },
    {
        name: ACTIVE_MODEL === "return" ? 'PROFITABILITY' : 'QUALITY',
        key: 'financial_profitability_grade'
    },
    { name: 'RISK', key: 'risk_grade' },
    { name: 'MOMENTUM', key: 'momentum_grade' },
    /* 6th factor — Risk was split into price risk (above) + balance-sheet quality.
       Same grade list and same "this grade or above" semantics as risk_grade. */
    { name: BALANCE_SHEET_FACTOR.labelUpper, key: BALANCE_SHEET_FACTOR.gradeKey },
];

const tableColumns = [
    { label: 'Ticker', key: 'ticker', sortType: 'string' },
    { label: 'FA Grade', key: 'overall_grade', sortType: 'grade' },
    { label: 'Valuation', key: 'valuation_grade', sortType: 'grade' },
    { label: 'Growth', key: 'growth_grade', sortType: 'grade' },
    // { label: 'Profitability', key: 'financial_profitability_grade',  sortType: 'grade'  },
    {
        label: ACTIVE_MODEL === "return" ? "Profitability" : "Quality",
        key: 'financial_profitability_grade',
        sortType: 'grade'
    },
    { label: 'Risk', key: 'risk_grade', sortType: 'grade' },
    { label: 'Momentum', key: 'momentum_grade', sortType: 'grade' },
    {
        label: BALANCE_SHEET_FACTOR.label,
        key: BALANCE_SHEET_FACTOR.gradeKey,
        sortType: 'grade',
    },
];

/* ─────────────────────────────────────────────────────────────
   SLIDER SX
   Visual trick to colour RIGHT side of thumb:
     • Rail  (full-width bg)          → BLUE   (represents included grades)
     • Track (MUI fills left→thumb)   → GREY   (masks the excluded left side)
   Net result: only thumb → right-end stays blue.
───────────────────────────────────────────────────────────── */
const BLUE = '#0096FF';
const buildSliderSx = (isDark) => {
    const grey = isDark ? '#2d3a4a' : '#D1D5DB';
    return {
        color: 'var(--color-primary)',
        height: 4,
        padding: '13px 0',
        '& .MuiSlider-thumb': {
            width: 14, height: 14,
            backgroundColor: BLUE,
            zIndex: 2,
            transition: 'box-shadow var(--transition-fast)',
            '&:hover': { boxShadow: '0 0 0 8px rgba(0,150,255,0.16)' },
        },
        '& .MuiSlider-rail': {
            opacity: 1,
            height: 4,
            backgroundColor: `${BLUE} !important`,  // full-width blue bg
        },
        '& .MuiSlider-track': {
            border: 'none',
            height: 4,
            backgroundColor: `${grey} !important`,  // grey masks the left/excluded portion
            opacity: 1,
        },
        '& .MuiSlider-mark': { display: 'none' },
        '& .MuiSlider-markLabel': {
            fontSize: 'var(--font-size-xs2)',
            color: 'var(--color-text-tertiary)',
            fontWeight: 'var(--font-weight-semibold)',
            top: 22,
        },
    };
};

/* ─────────────────────────────────────────────────────────────
   TABLE HEADER CELL SX
───────────────────────────────────────────────────────────── */
const theadCellSx = (isDark) => ({
    fontSize: 'var(--font-size-xs2)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: isDark ? '1px solid rgba(51,65,85,0.6)' : '1px solid var(--color-border-primary)',
    bgcolor: isDark ? 'rgba(26,35,50,0.95)' : 'var(--color-bg-tertiary)',
    py: 2,
    transition: 'color 0.15s ease',
});

/* ─────────────────────────────────────────────────────────────
   TOOLTIP CONTENT
───────────────────────────────────────────────────────────── */
const GradeTooltipContent = ({ val }) => (
    <Box sx={{ p: 0.5 }}>
        <Typography
            fontSize="var(--font-size-xs2)"
            fontWeight="var(--font-weight-bold)"
            sx={{ color: 'var(--color-text-primary)', mb: 0.75, opacity: 0.7 }}
        >
            Min {sliderToGrade[val]} — includes:
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={0.5}>
            {getIncludedGrades(val).map(g => (
                <Box key={g} sx={{
                    px: 0.75, py: 0.25,
                    borderRadius: 'var(--radius-sm)',
                    bgcolor: 'rgba(0,150,255,0.5)',
                    border: '1px solid rgba(255,255,255,0.2)',
                }}>
                    <Typography
                        fontSize="var(--font-size-xs2)"
                        fontWeight="var(--font-weight-bold)"
                        sx={{ color: '#fff', lineHeight: 1.4 }}
                    >
                        {g}
                    </Typography>
                </Box>
            ))}
        </Box>
    </Box>
);

const tooltipProps = (isDark) => ({
    tooltip: {
        sx: {
            bgcolor: isDark ? 'rgba(15,23,42,0.95)' : 'rgba(15,23,42,0.92)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(0,150,255,0.3)',
            borderRadius: 'var(--radius-lg)',
            p: 1.5, maxWidth: 280,
        },
    },
    arrow: { sx: { color: isDark ? 'rgba(15,23,42,0.95)' : 'rgba(15,23,42,0.92)' } },
});

/* ─────────────────────────────────────────────────────────────
   DRAG HINT  (shown while slider is still at default F)
───────────────────────────────────────────────────────────── */
const DragHint = () => (
    <Box
        display="flex" alignItems="center" gap={0.5} mt={0.75}
        sx={{
            animation: 'hintFade 2s ease-in-out infinite',
            '@keyframes hintFade': {
                '0%,100%': { opacity: 0.35 },
                '50%': { opacity: 0.75 },
            },
        }}
    >
        <Typography fontSize="var(--font-size-xs2)" sx={{ color: 'var(--color-text-muted)' }}>
            Drag right to select minimum FA grade
        </Typography>
        <EastIcon sx={{
            fontSize: 13, color: 'var(--color-text-muted)',
            animation: 'arrowSlide 1.4s ease-in-out infinite',
            '@keyframes arrowSlide': {
                '0%,100%': { transform: 'translateX(0)' },
                '50%': { transform: 'translateX(5px)' },
            },
        }} />
    </Box>
);

/* ─────────────────────────────────────────────────────────────
   GRADE BADGE  — supports full A+/A/A- … D+/D/D-/F set
───────────────────────────────────────────────────────────── */
const GradeBadge = ({ grade, getBg }) =>
    grade ? (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 36, py: 0.4, borderRadius: 'var(--radius-sm)', bgcolor: getBg(grade),
        }}>
            <Typography variant="chipText" fontWeight="var(--font-weight-bold)" sx={{ color: '#fff', lineHeight: 1 }}>
                {grade}
            </Typography>
        </Box>
    ) : (
        <Typography variant="chipText" sx={{ color: 'var(--color-text-muted)' }}>—</Typography>
    );

/* ─────────────────────────────────────────────────────────────
   APPLY BUTTON SX helper
   active = true  → vivid blue (changes pending)
   active = false → muted outline (already applied)
───────────────────────────────────────────────────────────── */
const applyBtnSx = (active, isDark) => ({
    fontSize: 'var(--font-size-xs2)',
    fontWeight: 'var(--font-weight-semibold)',
    textTransform: 'none',
    px: 2, py: 0.5,
    borderRadius: 'var(--radius-md)',
    transition: 'all var(--transition-fast)',
    ...(active
        ? {
            bgcolor: 'var(--color-primary)',
            color: '#fff',
            border: '1.5px solid var(--color-primary)',
            '&:hover': {
                bgcolor: 'var(--color-primary-dark)',
                transform: 'translateY(-1px)',
                boxShadow: 'var(--shadow-sm)',
            },
        }
        : {
            bgcolor: 'transparent',
            color: 'var(--color-text-muted)',
            border: isDark ? '1.5px solid rgba(51,65,85,0.4)' : '1.5px solid var(--color-border-primary)',
            pointerEvents: 'none',
        }
    ),
});

/* ─────────────────────────────────────────────────────────────
   FILTER SLIDER BOX
   Self-contained: owns pending slider value + its own Apply.
   appliedValue === null  → never applied → Apply always enabled
   appliedValue === number → compare with pending for dirty check
───────────────────────────────────────────────────────────── */
const FilterSliderBox = ({ category, appliedValue, onApply, isDark }) => {
    const [pending, setPending] = useState(GRADE_VAL_DEFAULT);

    // isDirty = true when:  (a) not yet applied  OR  (b) pending differs from applied
    const isDirty = appliedValue === APPLIED_NONE || pending !== appliedValue;

    // When parent clears all filters, reset pending too
    useEffect(() => {
        if (appliedValue === APPLIED_NONE) setPending(GRADE_VAL_DEFAULT);
        else setPending(appliedValue);
    }, [appliedValue]);

    return (
        <Box sx={{
            border: isDirty
                ? '1px solid rgba(0,150,255,0.45)'
                : isDark ? '1px solid rgba(51,65,85,0.5)' : '1px solid var(--color-border-secondary)',
            borderRadius: 'var(--radius-lg)',
            p: 2.5,
            bgcolor: isDark ? 'rgba(15,23,42,0.3)' : 'rgba(0,0,0,0.02)',
            transition: 'border-color var(--transition-fast)',
        }}>
            {/* Label row */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography
                    fontSize="var(--font-size-xs2)"
                    fontWeight="var(--font-weight-bold)"
                    sx={{ color: 'var(--color-text-secondary)' }}
                    letterSpacing="0.05em"
                    textTransform="uppercase"
                >
                    {category.name}
                </Typography>
                <Typography
                    fontSize="var(--font-size-xs2)"
                    fontWeight="var(--font-weight-bold)"
                    color={pending !== GRADE_VAL_DEFAULT ? 'var(--color-primary)' : 'var(--color-text-tertiary)'}
                >
                    Min: {sliderToGrade[pending]}
                </Typography>
            </Box>

            {/* Slider */}
            <Tooltip
                title={<GradeTooltipContent val={pending} />}
                placement="top"
                arrow
                componentsProps={tooltipProps(isDark)}
            >
                <Box>
                    <Slider
                        value={pending}
                        onChange={(_, v) => setPending(v)}
                        // min={1} max={5} step={1}
                        min={1} max={6} step={1}
                        marks={SLIDER_MARKS}
                        sx={buildSliderSx(isDark)}
                    />
                </Box>
            </Tooltip>

            {pending === GRADE_VAL_DEFAULT && <DragHint />}

            {/* Apply button */}
            <Box display="flex" justifyContent="flex-end" mt={1.5}>
                <Button
                    size="small"
                    startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                    onClick={() => { if (isDirty) onApply(category.key, pending); }}
                    sx={applyBtnSx(isDirty, isDark)}
                >
                    {isDirty ? 'Apply' : 'Applied'}
                </Button>
            </Box>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────
   OVERALL GRADE BOX  — same self-contained pattern
───────────────────────────────────────────────────────────── */
const OverallGradeBox = ({ appliedValue, onApply, isDark }) => {
    const [pending, setPending] = useState(GRADE_VAL_DEFAULT);

    const isDirty = appliedValue === APPLIED_NONE || pending !== appliedValue;

    useEffect(() => {
        if (appliedValue === APPLIED_NONE) setPending(GRADE_VAL_DEFAULT);
        else setPending(appliedValue);
    }, [appliedValue]);

    return (
        <Box gridColumn="span 2">
            {/* Label row */}
            <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography
                    fontSize="var(--font-size-xs2)"
                    fontWeight="var(--font-weight-semibold)"
                    sx={{ color: 'var(--color-text-secondary)' }}
                    letterSpacing="0.05em"
                    textTransform="uppercase"
                >
                    Overall FA Grade
                </Typography>
                <Typography
                    fontSize="var(--font-size-xs2)"
                    fontWeight="var(--font-weight-bold)"
                    color={pending !== GRADE_VAL_DEFAULT ? 'var(--color-primary)' : 'var(--color-text-tertiary)'}
                >
                    Min: {sliderToGrade[pending]}
                </Typography>
            </Box>

            {/* Slider */}
            <Tooltip
                title={<GradeTooltipContent val={pending} />}
                placement="top"
                arrow
                componentsProps={tooltipProps(isDark)}
            >
                <Box>
                    <Slider
                        value={pending}
                        onChange={(_, v) => setPending(v)}
                        // min={1} max={5} step={1}
                        min={1} max={6} step={1}
                        marks={SLIDER_MARKS}
                        sx={buildSliderSx(isDark)}
                    />
                </Box>
            </Tooltip>

            {pending === GRADE_VAL_DEFAULT && <DragHint />}

            {/* Apply button */}
            <Box display="flex" justifyContent="flex-end" mt={1.5}>
                <Button
                    size="small"
                    startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
                    onClick={() => { if (isDirty) onApply(pending); }}
                    sx={applyBtnSx(isDirty, isDark)}
                >
                    {isDirty ? 'Apply' : 'Applied'}
                </Button>
            </Box>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
const StocksScreenerByQuants = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    /* Applied (API-driving) state — null = not yet applied */
    const [selectedSector, setSelectedSector] = useState('');
    const [appliedOverallGrade, setAppliedOverallGrade] = useState(APPLIED_NONE);
    const [appliedFilters, setAppliedFilters] = useState(
        filterCategories.reduce((acc, cat) => ({ ...acc, [cat.key]: APPLIED_NONE }), {})
    );

    const [stocks, setStocks] = useState([]);
    const [scanning, setScanning] = useState(false);
    const [totalStockCount, setTotalStockCount] = useState(0);
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const searchRef = useRef(null);

    const [watchlistDialogOpen, setWatchlistDialogOpen] = useState(false);
    const [bulkMode, setBulkMode] = useState(false);
    const [selectedTickers, setSelectedTickers] = useState([]);
    const [singleTicker, setSingleTicker] = useState(null);

    /* ── Apply handlers (called from child slider boxes) ── */
    const applyOverallGrade = (val) => setAppliedOverallGrade(val);
    const applyFilter = (key, val) => setAppliedFilters(prev => ({ ...prev, [key]: val }));

    /* ── Clear all — reset applied + sentinel back to null ── */
    const clearAllFilters = () => {
        setAppliedFilters(filterCategories.reduce((acc, cat) => ({ ...acc, [cat.key]: APPLIED_NONE }), {}));
        setAppliedOverallGrade(APPLIED_NONE);
        setSelectedSector('');
    };

    /* Grade columns rendered as badges — everything except the ticker column.
       Includes the Balance-Sheet Quality column; its cells render the empty
       badge until the model backend starts pushing the new factor. */
    const gradeColumns = useMemo(
        () => tableColumns.filter(col => col.sortType === 'grade'),
        []
    );

    /* ticker + grade columns + Action (+ bulk checkbox) */
    const columnCount = tableColumns.length + 1 + (bulkMode ? 1 : 0);

    /* ── Active filter detection ── */
    const hasActiveFilters =
        !!selectedSector ||
        appliedOverallGrade !== APPLIED_NONE ||
        Object.values(appliedFilters).some(v => v !== APPLIED_NONE);

    const activeFilterCount = [
        selectedSector ? 1 : 0,
        appliedOverallGrade !== APPLIED_NONE ? 1 : 0,
        ...Object.values(appliedFilters).map(v => v !== APPLIED_NONE ? 1 : 0),
    ].reduce((a, b) => a + b, 0);

    /* ── Sorting ── */
    const handleSort = (columnKey) => {
        if (sortKey === columnKey) {
            if (sortDirection === 'asc') setSortDirection('desc');
            else { setSortKey(null); setSortDirection('asc'); }
        } else {
            setSortKey(columnKey);
            setSortDirection('asc');
        }
    };

    const sortedStocks = useMemo(() => {
        if (!sortKey) return stocks;
        const col = tableColumns.find(c => c.key === sortKey);
        if (!col) return stocks;
        return [...stocks].sort((a, b) => {
            if (col.sortType === 'grade') {
                const rA = gradeSortRank(a[sortKey]), rB = gradeSortRank(b[sortKey]);
                return sortDirection === 'asc' ? rA - rB : rB - rA;
            }
            if (col.sortType === 'string') {
                const vA = (a[sortKey] || '').toLowerCase();
                const vB = (b[sortKey] || '').toLowerCase();
                return sortDirection === 'asc' ? vA.localeCompare(vB) : vB.localeCompare(vA);
            }
            const nA = parseFloat(a[sortKey]) || 0, nB = parseFloat(b[sortKey]) || 0;
            return sortDirection === 'asc' ? nA - nB : nB - nA;
        });
    }, [stocks, sortKey, sortDirection]);

    const getSortIcon = (columnKey) => {
        if (sortKey !== columnKey) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.4, ml: 0.5 }} />;
        if (sortDirection === 'asc') return <ArrowUpwardIcon sx={{ fontSize: 16, color: 'var(--color-primary)', ml: 0.5 }} />;
        return <ArrowDownwardIcon sx={{ fontSize: 16, color: 'var(--color-primary)', ml: 0.5 }} />;
    };

    /* ── Watchlist ── */
    const openSingleAdd = (t) => { setSingleTicker(t); setSelectedTickers([]); setWatchlistDialogOpen(true); };
    const openBulkAdd = () => { if (!selectedTickers.length) return; setSingleTicker(null); setWatchlistDialogOpen(true); };

    const handleDialogClose = (success) => {
        setWatchlistDialogOpen(false);
        if (success) {
            setStocks(prev => prev.map(s => ({
                ...s,
                added: (singleTicker && s.ticker === singleTicker) || selectedTickers.includes(s.ticker)
                    ? true
                    : s.added,
            })));
        }
        setSingleTicker(null); setSelectedTickers([]); setBulkMode(false);
    };

    const toggleTicker = (t) =>
        setSelectedTickers(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

    useEffect(() => {
        const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) { } };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    useEffect(() => {
        if (watchlistDialogOpen) getAllPortfolios().then(r => { if (r?.success) { } });
    }, [watchlistDialogOpen]);

    /* ── Fetch  — fires whenever applied state or sector changes ── */
    const fetchStocksWithFilters = async () => {
        setScanning(true);
        try {
            const params = {};
            if (selectedSector) params.sector = selectedSector;
            // Include grade in payload even when F (val=1) — send the string 'F'
            if (appliedOverallGrade !== APPLIED_NONE)
                params.overall_grade = sliderToGrade[appliedOverallGrade];
            Object.keys(appliedFilters).forEach(key => {
                if (appliedFilters[key] !== APPLIED_NONE)
                    params[key] = sliderToGrade[appliedFilters[key]];
            });
            const res = await getStocksScreenerQuant(params);
            if (res?.success && Array.isArray(res.data?.stocks)) {
                setStocks(res.data.stocks.map(s => ({
                    ...s,
                    system_rating: s.overall_rating,
                    financial_profitability_grade: s.financial_profit_grade,
                    added: false,
                })));
                setTotalStockCount(res.data.total_stock_count ?? 0);
            } else {
                setStocks([]); setTotalStockCount(0);
            }
        } catch { setStocks([]); }
        finally { setScanning(false); }
    };

    useEffect(() => {
        const t = setTimeout(() => {
            if (hasActiveFilters) fetchStocksWithFilters();
            else setStocks([]);
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSector, appliedOverallGrade, JSON.stringify(appliedFilters)]);

    /* ── Grade badge colours — full A+/A/A- … F set ── */
    // const getGradeBg = (grade) => {
    //     const map = {
    //         'A+': theme.palette.grades.aPlus,  A: theme.palette.grades.a,   'A-': theme.palette.grades.aMinus,
    //         'B+': theme.palette.grades.bPlus,  B: theme.palette.grades.b,   'B-': theme.palette.grades.bMinus,
    //         'C+': theme.palette.grades.cPlus,  C: theme.palette.grades.c,   'C-': theme.palette.grades.cMinus,
    //         'D+': theme.palette.grades.dPlus,  D: theme.palette.grades.d,   'D-': theme.palette.grades.dMinus,
    //         F:    theme.palette.grades.f,
    //     };
    //     return map[grade] ?? theme.palette.grades.unknown;
    // };

    const getGradeBg = (grade) => {
        const map = {
            'A+': theme.palette.grades.aPlus, A: theme.palette.grades.a, 'A-': theme.palette.grades.aMinus,
            'B+': theme.palette.grades.bPlus, B: theme.palette.grades.b, 'B-': theme.palette.grades.bMinus,
            'C+': theme.palette.grades.cPlus, C: theme.palette.grades.c, 'C-': theme.palette.grades.cMinus,
            'D+': theme.palette.grades.dPlus, D: theme.palette.grades.d, 'D-': theme.palette.grades.dMinus,
            'E+': theme.palette.grades.ePlus, E: theme.palette.grades.e, 'E-': theme.palette.grades.eMinus,  // ← new
            F: theme.palette.grades.f,
        };
        return map[grade] ?? theme.palette.grades.unknown;
    };

    /* ─────────────────────────────────────────────────────────────
       RENDER
    ───────────────────────────────────────────────────────────── */
    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: isDark ? '#080d18' : 'var(--color-bg-primary)',
            backgroundImage: isDark
                ? 'radial-gradient(ellipse at 15% 0%, rgba(0,150,255,0.06) 0%, transparent 55%), radial-gradient(ellipse at 85% 100%, rgba(59,130,246,0.04) 0%, transparent 50%)'
                : 'none',
            px: { xs: 2, md: 4 },
            py: 3.5,
            transition: 'background-color var(--transition-normal)',
        }}>

            {/* ── Header ── */}
            <Box mb={3.5}>
                <Typography
                    variant="title"
                    fontWeight="var(--font-weight-bold)"
                    sx={{ color: 'var(--color-text-primary)', display: 'block' }}
                    letterSpacing="-0.02em"
                    mb={0.5}
                >
                    Stock Screener — FA Based Analysis
                </Typography>
                <Typography variant="subtitle" fontWeight="var(--font-weight-normal)" sx={{ color: 'var(--color-text-muted)' }}>
                    Multi-factor S&P 500 screening engine powered by quantitative analysis
                </Typography>
            </Box>

            {market === "ALL" ? (<>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        p: 3,
                        borderRadius: "var(--radius-lg)",
                        border: "1px solid var(--color-info)",
                        bgcolor: "var(--color-info-light)",
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "var(--radius-md)",
                            bgcolor: "var(--color-info)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <Typography sx={{ color: "#fff", fontWeight: "var(--font-weight-black)", fontSize: "var(--font-size-lg)" }}>
                            !
                        </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column">
                        <Typography variant="tableHeader" fontWeight="var(--font-weight-bold)" sx={{ color: "var(--color-text-primary)" }} mb={0.5}>
                            Market Selection Required
                        </Typography>
                        <Typography variant="paragraph" sx={{ color: "var(--color-text-secondary)" }}>
                            The Stock Screener is not available in <strong>All Markets</strong> mode. Please select a specific market (e.g. <strong>US</strong> or <strong>TR</strong>) from the market selector to access the screener.
                        </Typography>
                    </Box>
                </Box>
            </>) : (<>
                {/* ── Filter Card ── */}
                <Box sx={{
                    bgcolor: isDark ? 'rgba(30,41,59,0.5)' : 'var(--color-surface)',
                    backdropFilter: 'blur(20px)',
                    border: isDark ? '1px solid rgba(51,65,85,0.6)' : '1px solid var(--color-border-primary)',
                    borderRadius: 'var(--radius-xl)',
                    p: 3.5, mb: 1,
                    boxShadow: isDark ? 'var(--shadow-lg)' : 'var(--shadow-md)',
                    transition: 'all var(--transition-normal)',
                }}>

                    {/* Card header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Typography
                                variant="tableHeader"
                                fontWeight="var(--font-weight-bold)"
                                sx={{ color: 'var(--color-text-primary)' }}
                                letterSpacing="-0.01em"
                            >
                                Filter Criteria
                            </Typography>
                            {hasActiveFilters && (
                                <Chip
                                    label={`${activeFilterCount} active`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(59,130,246,0.15)',
                                        color: 'var(--color-info)',
                                        fontSize: 'var(--font-size-xs2)',
                                        height: 24,
                                        fontWeight: 'var(--font-weight-bold)',
                                        border: '1px solid rgba(59,130,246,0.3)',
                                    }}
                                />
                            )}
                        </Box>
                        <Button
                            startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                            onClick={clearAllFilters}
                            disabled={!hasActiveFilters}
                            sx={{
                                fontSize: 'var(--font-size-xs2)',
                                fontWeight: 'var(--font-weight-semibold)',
                                color: hasActiveFilters ? 'var(--color-text-secondary)' : 'var(--color-text-muted)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                minWidth: 'auto',
                                px: 2, py: 0.5,
                                transition: 'all var(--transition-fast)',
                                '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', color: 'var(--color-primary)' },
                            }}
                        >
                            Clear All
                        </Button>
                    </Box>

                    {/* Row 1: Sector (immediate) + Overall Grade (own Apply) */}
                    <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={3} mb={3}>

                        {/* Sector — fires fetch immediately on change */}
                        <Box>
                            <Typography
                                fontSize="var(--font-size-xs2)"
                                fontWeight="var(--font-weight-semibold)"
                                sx={{ color: 'var(--color-text-secondary)' }}
                                letterSpacing="0.05em" textTransform="uppercase" mb={1.5}
                            >
                                Sector
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={selectedSector}
                                    displayEmpty
                                    onChange={(e) => setSelectedSector(e.target.value)}
                                    sx={{
                                        fontSize: 'var(--font-size-sm)',
                                        bgcolor: isDark ? 'rgba(15,23,42,0.6)' : 'var(--color-bg-secondary)',
                                        color: 'var(--color-text-primary)',
                                        borderRadius: 'var(--radius-lg)',
                                        transition: 'all var(--transition-fast)',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: isDark ? 'rgba(51,65,85,0.6)' : 'var(--color-border-secondary)',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-primary)' },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'var(--color-primary)', borderWidth: '2px',
                                        },
                                        '& .MuiSvgIcon-root': { color: 'var(--color-text-secondary)' },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: isDark ? 'rgba(15,23,42,0.95)' : 'var(--color-surface-hover)',
                                                backdropFilter: 'blur(20px)',
                                                border: isDark ? '1px solid rgba(51,65,85,0.6)' : '1px solid var(--color-border-primary)',
                                                borderRadius: 'var(--radius-lg)',
                                                mt: 0.5, maxHeight: 240,
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="">
                                        <Typography fontSize="var(--font-size-sm)" sx={{ color: 'var(--color-text-tertiary)' }}>
                                            All Sectors
                                        </Typography>
                                    </MenuItem>
                                    {sectors.map(sector => (
                                        <MenuItem
                                            key={sector} value={sector}
                                            sx={{
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--color-text-primary)',
                                                py: 1.5,
                                                '&:hover': { bgcolor: isDark ? 'rgba(51,65,85,0.5)' : 'var(--color-surface-active)' },
                                            }}
                                        >
                                            {sector}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Overall FA Grade — self-contained with Apply */}
                        <OverallGradeBox
                            appliedValue={appliedOverallGrade}
                            onApply={applyOverallGrade}
                            isDark={isDark}
                        />
                    </Box>

                    {/* Row 2: Per-category filter sliders — each self-contained */}
                    <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2.5}>
                        {filterCategories.map(cat => (
                            <FilterSliderBox
                                key={cat.key}
                                category={cat}
                                appliedValue={appliedFilters[cat.key]}
                                onApply={applyFilter}
                                isDark={isDark}
                            />
                        ))}
                    </Box>
                </Box>

                {/* ── Bulk Mode Toggle ── */}
                {hasActiveFilters && (
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            onClick={() => { setBulkMode(p => !p); setSelectedTickers([]); }}
                            sx={{
                                mb: 1,
                                bgcolor: bulkMode ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)',
                                color: bulkMode ? 'var(--color-error)' : 'var(--color-info)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 'var(--font-weight-semibold)',
                                textTransform: 'none',
                                px: 3, py: 1,
                                borderRadius: 'var(--radius-lg)',
                                border: bulkMode ? '1.5px solid rgba(239,68,68,0.3)' : '1.5px solid rgba(59,130,246,0.3)',
                                transition: 'all var(--transition-fast)',
                                '&:hover': {
                                    bgcolor: bulkMode ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)',
                                    borderColor: bulkMode ? 'var(--color-error)' : 'var(--color-info)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: bulkMode ? '0 4px 12px rgba(239,68,68,0.2)' : '0 4px 12px rgba(59,130,246,0.2)',
                                },
                            }}
                        >
                            {bulkMode ? 'Cancel Selection' : '+ Bulk add to Watchlist'}
                        </Button>
                    </Box>
                )}

                {/* ── Results Table ── */}
                <Box sx={{
                    bgcolor: isDark ? 'rgba(30,41,59,0.5)' : 'var(--color-surface)',
                    backdropFilter: 'blur(20px)',
                    border: isDark ? '1px solid rgba(51,65,85,0.6)' : '1px solid var(--color-border-primary)',
                    borderRadius: 'var(--radius-xl)',
                    overflow: 'hidden', mb: 3, maxHeight: 520,
                    display: 'flex', flexDirection: 'column',
                    boxShadow: isDark ? 'var(--shadow-lg)' : 'var(--shadow-md)',
                    transition: 'all var(--transition-normal)',
                }}>
                    {/* Bulk selection bar */}
                    {bulkMode && selectedTickers.length > 0 && (
                        <Box sx={{
                            p: 2.5,
                            bgcolor: isDark ? 'rgba(15,23,42,0.8)' : 'var(--color-bg-secondary)',
                            borderBottom: isDark ? '1px solid rgba(51,65,85,0.6)' : '1px solid var(--color-border-primary)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <Typography
                                variant="tableHeader"
                                sx={{ color: 'var(--color-text-primary)' }}
                                fontWeight="var(--font-weight-semibold)"
                            >
                                {selectedTickers.length} stock{selectedTickers.length !== 1 ? 's' : ''} selected
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={openBulkAdd}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    px: 3, py: 1,
                                    bgcolor: 'var(--color-primary)',
                                    borderRadius: 'var(--radius-lg)',
                                    transition: 'all var(--transition-fast)',
                                    '&:hover': { bgcolor: 'var(--color-primary-dark)', transform: 'translateY(-1px)', boxShadow: 'var(--shadow-md)' },
                                }}
                            >
                                Add Selected to Watchlist
                            </Button>
                        </Box>
                    )}

                    {/* Table */}
                    <Box sx={{ overflowY: 'auto', flex: 1 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {bulkMode && (
                                        <TableCell sx={theadCellSx(isDark)}>
                                            <input
                                                type="checkbox"
                                                checked={stocks.length > 0 && selectedTickers.length === stocks.length}
                                                onChange={(e) =>
                                                    setSelectedTickers(e.target.checked ? stocks.map(s => s.ticker) : [])
                                                }
                                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                                            />
                                        </TableCell>
                                    )}
                                    {tableColumns.map(col => (
                                        <TableCell
                                            key={col.key}
                                            onClick={() => handleSort(col.key)}
                                            sx={{
                                                ...theadCellSx(isDark),
                                                color: sortKey === col.key ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                '&:hover': { color: 'var(--color-primary)' },
                                            }}
                                        >
                                            <Box display="flex" alignItems="center">
                                                {col.label}{getSortIcon(col.key)}
                                            </Box>
                                        </TableCell>
                                    ))}
                                    <TableCell sx={theadCellSx(isDark)}>
                                        {bulkMode ? '' : 'Action'}
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {scanning ? (
                                    <TableRow>
                                        <TableCell colSpan={columnCount} sx={{ py: 8, textAlign: 'center' }}>
                                            <CircularProgress size={40} sx={{ color: 'var(--color-primary)' }} />
                                            <Typography
                                                variant="tableHeader"
                                                sx={{ color: 'var(--color-text-tertiary)' }}
                                                mt={2}
                                                fontWeight="var(--font-weight-medium)"
                                                display="block"
                                            >
                                                Scanning stocks...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : stocks.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columnCount} sx={{ py: 8, textAlign: 'center' }}>
                                            <Typography
                                                variant="tableHeader"
                                                sx={{ color: 'var(--color-text-tertiary)' }}
                                                fontWeight="var(--font-weight-medium)"
                                            >
                                                {hasActiveFilters
                                                    ? 'No stocks match the current filter criteria'
                                                    : 'Apply filters above to scan for stocks.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedStocks.map((stock, idx) => (
                                        <TableRow
                                            key={stock.ticker}
                                            sx={{
                                                transition: 'all var(--transition-fast)',
                                                '&:hover': { bgcolor: isDark ? 'rgba(26,35,50,0.6)' : 'var(--color-surface-hover)' },
                                                borderBottom: idx === sortedStocks.length - 1
                                                    ? 'none'
                                                    : isDark ? '1px solid rgba(51,65,85,0.4)' : '1px solid var(--color-border-primary)',
                                            }}
                                        >
                                            {bulkMode && (
                                                <TableCell sx={{ py: 2 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedTickers.includes(stock.ticker)}
                                                        onChange={() => toggleTicker(stock.ticker)}
                                                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell sx={{ py: 2 }}>
                                                <Typography
                                                    variant="chipText"
                                                    sx={{ color: 'var(--color-primary)' }}
                                                    letterSpacing="-0.01em"
                                                >
                                                    {stock.ticker}
                                                </Typography>
                                            </TableCell>
                                            {/* Driven off tableColumns so header and cells
                                                can never fall out of sync when the 6th factor
                                                appears or disappears. */}
                                            {gradeColumns.map(col => (
                                                <TableCell key={col.key} sx={{ py: 2 }}>
                                                    <GradeBadge grade={stock[col.key]} getBg={getGradeBg} />
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ py: 2 }}>
                                                {stock.added ? (
                                                    <Chip
                                                        icon={<CheckIcon sx={{ fontSize: 16 }} />}
                                                        label="ADDED"
                                                        sx={{
                                                            bgcolor: 'rgba(16,185,129,0.15)',
                                                            color: 'var(--color-success)',
                                                            fontSize: 'var(--font-size-xs2)',
                                                            height: 28,
                                                            fontWeight: 'var(--font-weight-bold)',
                                                            border: '1px solid rgba(16,185,129,0.3)',
                                                        }}
                                                    />
                                                ) : bulkMode ? null : (
                                                    <Button
                                                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                                        onClick={() => openSingleAdd(stock.ticker)}
                                                        sx={{
                                                            fontSize: 'var(--font-size-xs2)',
                                                            fontWeight: 'var(--font-weight-semibold)',
                                                            textTransform: 'none',
                                                            color: 'var(--color-primary)',
                                                            transition: 'all var(--transition-fast)',
                                                            '&:hover': { bgcolor: 'rgba(0,150,255,0.1)' },
                                                        }}
                                                    >
                                                        Watchlist
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>

                {/* ── Analytics Card ── */}
                <Box sx={{
                    bgcolor: isDark ? 'rgba(30,41,59,0.5)' : 'var(--color-surface)',
                    backdropFilter: 'blur(20px)',
                    border: isDark ? '1px solid rgba(51,65,85,0.6)' : '1px solid var(--color-border-primary)',
                    borderRadius: 'var(--radius-xl)',
                    p: 4,
                    boxShadow: isDark ? 'var(--shadow-lg)' : 'var(--shadow-md)',
                    transition: 'all var(--transition-normal)',
                }}>
                    <Typography
                        variant="subtitle"
                        fontWeight="var(--font-weight-semibold)"
                        sx={{ color: 'var(--color-text-primary)' }}
                        mb={1} letterSpacing="-0.01em" display="block"
                    >
                        Index Correlation Analytics
                    </Typography>
                    <Typography
                        variant="paragraph"
                        sx={{ color: 'var(--color-text-secondary)' }}
                        mb={4} lineHeight={1.7} maxWidth={900} display="block"
                    >
                        All quantitative ratings are synthesised using advanced reasoning over real-time financial
                        fundamentals and momentum trends.
                    </Typography>

                    <Box display="flex" gap={8}>
                        <Box>
                            <Typography
                                variant="tableHeader"
                                sx={{ color: 'var(--color-text-tertiary)' }}
                                fontWeight="var(--font-weight-bold)"
                                textTransform="uppercase" letterSpacing="0.08em" mb={1.5} display="block"
                            >
                                Total Screened
                            </Typography>
                            <Typography sx={{
                                background: isDark
                                    ? 'linear-gradient(135deg,#e2e8f0 0%,#94a3b8 100%)'
                                    : 'linear-gradient(135deg,#1e293b 0%,#475569 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontSize: 'var(--font-size-5xl)',
                                fontWeight: 'var(--font-weight-black)',
                            }}>
                                {stocks.length}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                variant="tableHeader"
                                sx={{ color: 'var(--color-text-tertiary)' }}
                                fontWeight="var(--font-weight-bold)"
                                textTransform="uppercase" letterSpacing="0.08em" mb={1.5} display="block"
                            >
                                Total Index Stocks
                            </Typography>
                            <Typography sx={{
                                background: 'linear-gradient(135deg,#3b82f6 0%,#1d4ed8 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontSize: 'var(--font-size-5xl)',
                                fontWeight: 'var(--font-weight-black)',
                            }}>
                                {totalStockCount}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </>)}

            <AddToWatchlistDialog
                open={watchlistDialogOpen}
                ticker={singleTicker}
                tickers={selectedTickers}
                onClose={handleDialogClose}
            />
        </Box>
    );
};

export default StocksScreenerByQuants;